/**
 * Hierarchical filter function for TanStack Table
 * Shows parent rows if any child/descendant matches the filter
 */

/**
 * Check if a row matches the filter by searching all column values
 */
function rowMatchesFilter (row, filterValue) {
  if (!filterValue) return true

  const searchLower = filterValue.toLowerCase()

  // Search all values in the row
  const rowValues = Object.values(row.original)

  for (const value of rowValues) {
    if (value == null) continue

    // Convert to string and check for match
    const stringValue = String(value).toLowerCase()
    if (stringValue.includes(searchLower)) {
      return true
    }
  }

  return false
}

/**
 * Recursively check if any descendant matches the filter
 */
function hasMatchingDescendant (row, filterValue, getSubRows) {
  const subRows = getSubRows(row.original)
  if (!subRows || subRows.length === 0) return false

  for (const subRow of subRows) {
    // Check if this child matches
    if (rowMatchesFilter({ original: subRow }, filterValue)) {
      return true
    }

    // Recursively check descendants
    if (hasMatchingDescendant({ original: subRow }, filterValue, getSubRows)) {
      return true
    }
  }

  return false
}

/**
 * Check if any ancestor matches the filter
 * This allows children to be shown even if they don't match,
 * as long as their parent/ancestor matches
 */
function hasMatchingAncestor (row, filterValue) {
  let currentRow = row

  // Walk up the tree checking each ancestor
  while (currentRow.parentId !== undefined) {
    const parent = currentRow.getParentRow()
    if (!parent) break

    if (rowMatchesFilter(parent, filterValue)) {
      return true
    }

    currentRow = parent
  }

  return false
}

/**
 * Hierarchical filter function for TanStack Table
 * Shows a row if:
 * - The row itself matches
 * - Any ancestor matches (shows all descendants of matching rows)
 * - Any descendant matches (shows parents of matching rows)
 */
export function hierarchicalFilterFn (row, columnId, filterValue, addMeta) {
  if (!filterValue || filterValue.trim() === '') return true

  // Check if current row matches
  if (rowMatchesFilter(row, filterValue)) {
    return true
  }

  // Check if any ancestor matches (show children of matched parents)
  if (hasMatchingAncestor(row, filterValue)) {
    return true
  }

  // Check if any descendant matches (show parents of matched children)
  // Use the getSubRows from table config
  const getSubRows = (rowData) => rowData.subRows || rowData.data || []
  return hasMatchingDescendant(row, filterValue, getSubRows)
}

// Mark as global filter (searches all columns)
hierarchicalFilterFn.autoRemove = (val) => !val
